import { Track } from "livekit-client"
import * as React from "react"
import { supportsScreenSharing } from "@livekit/components-core"
import {
    RoomAudioRenderer,
    useLocalParticipant,
    useLocalParticipantPermissions,
    useMaybeLayoutContext,
    usePersistentUserChoices,
} from "@livekit/components-react"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"
import { TrackToggleM } from "./TrackToggleM"
import { SCREEN_SHARE_CAPTURE_OPTIONS } from "../../../engine/live/room/screenShareToggle"
import { TrackToggleWithDeviceSelectM } from "./TrackToggleWithDeviceSelectM"
import { Pressable, Text, View, useWindowDimensions } from "react-native"
import { theme } from "../../theme/theme"

// Fond sombre des bulles de la barre — la pointe doit rester dans la même
// couleur que la carte, d'où la constante partagée.
const TOOLTIP_BACKGROUND = "#1a1a1a"

const WarningBadge = () => (
    <View
        pointerEvents="none"
        style={{
            position: "absolute",
            top: 2,
            right: -4,
            backgroundColor: theme.colors.warning,
            borderRadius: 10,
            width: 20,
            height: 20,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20,
            borderWidth: 2,
            borderColor: "white",
        }}
    >
        <FontAwesome6 name="exclamation" size={10} color="white" />
    </View>
)

// Ancrage commun aux bulles de la barre : au-dessus du bouton, centré sur lui.
const TooltipAnchor = ({
    zIndex,
    children,
}: {
    zIndex: number
    children: React.ReactNode
}) => (
    <View
        style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            alignItems: "center",
            paddingBottom: 10,
            zIndex,
        }}
    >
        {children}
    </View>
)

// Petite pointe sous la bulle, dans la couleur de sa carte.
const TooltipArrow = () => (
    <View
        style={{
            position: "absolute",
            bottom: -6,
            left: 0,
            right: 0,
            alignItems: "center",
        }}
    >
        <View
            style={{
                width: 0,
                height: 0,
                borderLeftWidth: 6,
                borderLeftColor: "transparent",
                borderRightWidth: 6,
                borderRightColor: "transparent",
                borderTopWidth: 6,
                borderTopColor: TOOLTIP_BACKGROUND,
            }}
        />
    </View>
)

const WarningTooltip = ({
    label,
    onPress,
    isVisible,
}: {
    label: string
    onPress?: () => void
    isVisible: boolean
}) => {
    if (!isVisible) return null
    return (
        <TooltipAnchor zIndex={30}>
            <View
                style={{
                    backgroundColor: TOOLTIP_BACKGROUND,
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 12,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 8,
                    minWidth: 200,
                    position: "relative",
                }}
            >
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <FontAwesome6 name="triangle-exclamation" size={13} color={theme.colors.warning} />
                    <Text style={{ color: "#fff", fontSize: 12, fontFamily: "Montserrat-SemiBold" }}>
                        {label}
                    </Text>
                </View>
                <Pressable
                    onPress={onPress}
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                        paddingVertical: 4,
                    }}
                >
                    <FontAwesome6 name="stethoscope" size={11} color={theme.colors.linkLight} />
                    <Text style={{ color: theme.colors.linkLight, fontSize: 12, fontFamily: "Montserrat-Medium" }}>
                        Ouvrir le diagnostic
                    </Text>
                </Pressable>
                <TooltipArrow />
            </View>
        </TooltipAnchor>
    )
}

const ObstructedTooltip = ({
    isVisible,
    onDismiss,
}: {
    isVisible: boolean
    onDismiss?: () => void
}) => {
    if (!isVisible) return null
    return (
        <TooltipAnchor zIndex={31}>
            <View
                style={{
                    backgroundColor: TOOLTIP_BACKGROUND,
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingLeft: 14,
                    paddingRight: onDismiss ? 36 : 14,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 12,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 6,
                    minWidth: 240,
                    maxWidth: 300,
                    position: "relative",
                }}
            >
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <FontAwesome6 name="triangle-exclamation" size={14} color={theme.colors.warning} />
                    <Text style={{ color: "#fff", fontSize: 13, fontFamily: "Montserrat-SemiBold" }}>
                        Ta caméra est peut-être masquée
                    </Text>
                </View>
                <Text
                    style={{
                        color: "#ddd",
                        fontSize: 12,
                        fontFamily: "Montserrat-Regular",
                        lineHeight: 16,
                    }}
                >
                    Vérifie qu'aucun cache ne recouvre l'objectif.
                </Text>
                {onDismiss && (
                    <Pressable
                        onPress={onDismiss}
                        accessibilityLabel="Fermer"
                        style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            padding: 6,
                        }}
                    >
                        <FontAwesome6 name="xmark" size={14} color="#aaa" />
                    </Pressable>
                )}
                <TooltipArrow />
            </View>
        </TooltipAnchor>
    )
}

/** @public */
export type ControlBarControls = {
    microphone?: boolean
    camera?: boolean
    chat?: boolean
    screenShare?: boolean
    leave?: boolean
    settings?: boolean
}

/** @public */
export interface ControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
    onDeviceError?: (error: { source: Track.Source; error: Error }) => void
    variation?: "minimal" | "verbose" | "textOnly"
    controls?: ControlBarControls
    miniMode?: boolean
    pinnedMode?: boolean
    setPinnedMode?: (value: boolean) => void
    isSoundMode?: boolean
    onOffCam?: (value: boolean) => void
    smallMode?: boolean
    /**
     * If `true`, the user's device choices will be persisted.
     * This will enables the user to have the same device choices when they rejoin the room.
     * @defaultValue true
     * @alpha
     */
    saveUserChoices?: boolean
    width?: string
    skipAudioRenderer?: boolean
    micWarning?: boolean
    camWarning?: boolean
    onMicWarningPress?: () => void
    onCamWarningPress?: () => void
    onOpenHelp?: () => void
    camObstructedWarning?: boolean
    camObstructedPopoverVisible?: boolean
    onDismissObstructedPopover?: () => void
}

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components-web like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function ControlBarM({
    variation,
    controls,
    saveUserChoices = true,
    onDeviceError,
    miniMode,
    isSoundMode,
    onOffCam,
    width = "100%",
    smallMode = false,
    skipAudioRenderer = false,
    micWarning,
    camWarning,
    onMicWarningPress,
    onCamWarningPress,
    onOpenHelp,
    camObstructedWarning,
    camObstructedPopoverVisible,
    onDismissObstructedPopover,
}: ControlBarProps) {
    const [micHover, setMicHover] = React.useState(false)
    const [camHover, setCamHover] = React.useState(false)
    // Panne caméra avérée ou objectif soupçonné masqué : même mise en avant
    // du bouton (contour + pastille), deux messages distincts.
    const showCamWarningRing = camWarning || camObstructedWarning
    const [isChatOpen, setIsChatOpen] = React.useState(false)
    const layoutContext = useMaybeLayoutContext()
    React.useEffect(() => {
        if (layoutContext?.widget.state?.showChat !== undefined) {
            setIsChatOpen(layoutContext?.widget.state?.showChat)
        }
    }, [layoutContext?.widget.state?.showChat])
    const { width: windowWidth } = useWindowDimensions()
    const isTooLittleSpace = windowWidth <= (isChatOpen ? 1000 : 760)

    const defaultVariation = isTooLittleSpace ? "minimal" : "verbose"
    variation ??= defaultVariation

    const visibleControls = { leave: false, layout: true, ...controls }

    const localPermissions = useLocalParticipantPermissions()

    const { isCameraEnabled } = useLocalParticipant()

    if (!localPermissions) {
        visibleControls.camera = false
        visibleControls.chat = false
        visibleControls.microphone = false
        visibleControls.screenShare = false
    } else {
        visibleControls.camera ??= localPermissions.canPublish
        visibleControls.microphone ??= localPermissions.canPublish
        visibleControls.screenShare ??= localPermissions.canPublish
        visibleControls.chat ??=
            localPermissions.canPublishData && controls?.chat
    }

    const showIcon = React.useMemo(
        () => variation === "minimal" || variation === "verbose",
        [variation]
    )

    const browserSupportsScreenSharing = supportsScreenSharing()

    const { saveAudioInputEnabled, saveVideoInputEnabled } =
        usePersistentUserChoices({ preventSave: !saveUserChoices })

    const microphoneOnChange = React.useCallback(
        (enabled: boolean, isUserInitiated: boolean) => {
            if (isUserInitiated) {
                saveAudioInputEnabled(enabled)
            }
        },
        [saveAudioInputEnabled]
    )

    const cameraOnChange = React.useCallback(
        (enabled: boolean, isUserInitiated: boolean) => {
            if (isUserInitiated) {

                saveVideoInputEnabled(enabled)
            }
        },
        [saveVideoInputEnabled]
    )

    React.useEffect(() => {
        if (isCameraEnabled) {
            onOffCam?.(true)
        } else {
            onOffCam?.(false)
        }
    }, [isCameraEnabled])

    return (
        <View
            style={{
                width,
                height: 70,
                zIndex: 10,
                flexDirection: "row",
                justifyContent: "space-evenly",
                alignItems: "center",
                alignSelf: "center",
                gap: 10,
                marginRight: 5,
            }}
        >
            {/* The RoomAudioRenderer takes care of room-wide audio for you. */}
            {/* Use volume instead of muted to avoid server-side unsubscribe (pub.setEnabled) */}
            {!skipAudioRenderer && <RoomAudioRenderer volume={isSoundMode ? 1 : 0} />}
            {visibleControls.microphone && (isSoundMode || !miniMode) && (
                <View
                    onMouseEnter={() => micWarning && setMicHover(true)}
                    onMouseLeave={() => setMicHover(false)}
                    style={{
                        justifyContent: "center",
                        flexDirection: "row",
                        alignItems: "center",
                        position: "relative",
                        overflow: "visible",
                    }}
                >
                    <View
                        style={{
                            opacity: !isSoundMode ? 0.5 : 1,
                            flexDirection: "row",
                            alignItems: "center",
                            position: "relative",
                            borderWidth: micWarning ? 2 : 0,
                            borderColor: theme.colors.warning,
                            borderStyle: "solid",
                            borderRadius: 24,
                            padding: micWarning ? 2 : 0,
                        }}
                    >
                        <TrackToggleWithDeviceSelectM
                            source={Track.Source.Microphone}
                            showIcon={showIcon}
                            onChange={microphoneOnChange}
                            disabled={!isSoundMode}
                            isSoundMode={isSoundMode}
                            captureOptions={{
                                echoCancellation: true,
                                noiseSuppression: true,
                            }}
                            onDeviceError={(error) =>
                                onDeviceError?.({
                                    source: Track.Source.Microphone,
                                    error,
                                })
                            }
                            onOpenHelp={onOpenHelp}
                        />
                        {micWarning && <WarningBadge />}
                    </View>
                    <WarningTooltip
                        label="Problème de micro détecté"
                        onPress={onMicWarningPress}
                        isVisible={micWarning && micHover}
                    />
                </View>
            )}
            {visibleControls.camera && (
                <View
                    // Toujours enregistrer le survol : l'obstruction est
                    // détectée ~9 s après que le tuteur a couvert l'objectif,
                    // souvent alors que sa souris est déjà posée sur le bouton
                    // caméra. Un garde sur le drapeau d'alerte laisserait
                    // camHover à false pour tout le reste de la séance. Les
                    // deux tooltips sont déjà gardés par leur propre drapeau.
                    onMouseEnter={() => setCamHover(true)}
                    onMouseLeave={() => setCamHover(false)}
                    style={{
                        justifyContent: "center",
                        flexDirection: "row",
                        alignItems: "center",
                        position: "relative",
                        overflow: "visible",
                        borderWidth: showCamWarningRing ? 2 : 0,
                        borderColor: theme.colors.warning,
                        borderStyle: "solid",
                        borderRadius: 24,
                        padding: showCamWarningRing ? 2 : 0,
                    }}
                >
                    <TrackToggleWithDeviceSelectM
                        source={Track.Source.Camera}
                        showIcon={showIcon}
                        onChange={cameraOnChange}
                        isSoundMode={isSoundMode}
                        onDeviceError={(error) =>
                            onDeviceError?.({
                                source: Track.Source.Camera,
                                error,
                            })
                        }
                        onOpenHelp={onOpenHelp}
                    />
                    {showCamWarningRing && <WarningBadge />}
                    <WarningTooltip
                        label="Problème de caméra détecté"
                        onPress={onCamWarningPress}
                        isVisible={camWarning && camHover}
                    />
                    {/*
                        Both tooltips share the same anchor (absolute, bottom
                        100%), so an actual camera failure takes precedence
                        over the obstruction hint. Once the popover is
                        dismissed the badge stays lit, so hovering brings the
                        explanation back — without the close button, which
                        only makes sense on the auto-shown popover.
                    */}
                    <ObstructedTooltip
                        isVisible={
                            !camWarning &&
                            !!camObstructedWarning &&
                            (!!camObstructedPopoverVisible || camHover)
                        }
                        onDismiss={
                            camObstructedPopoverVisible
                                ? () => {
                                      // Le popover est un enfant du conteneur
                                      // survolé : sans ça, camHover le
                                      // rouvrirait dans la foulée et la croix
                                      // paraîtrait sans effet.
                                      setCamHover(false)
                                      onDismissObstructedPopover?.()
                                  }
                                : undefined
                        }
                    />
                </View>
            )}
            {visibleControls.screenShare &&
                browserSupportsScreenSharing &&
                !miniMode &&
                !smallMode &&
                windowWidth > 700 && (
                    <View
                        style={{
                            justifyContent: "center",
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        <TrackToggleM
                            source={Track.Source.ScreenShare}
                            captureOptions={SCREEN_SHARE_CAPTURE_OPTIONS}
                            showIcon={showIcon}
                            onChange={() => {}}
                            onDeviceError={(error) =>
                                onDeviceError?.({
                                    source: Track.Source.ScreenShare,
                                    error,
                                })
                            }
                        ></TrackToggleM>
                    </View>
                )}
        </View>
    )
}
